import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { HubConnection, HubConnectionBuilder, HttpTransportType } from "@microsoft/signalr";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";

export type LogDeliveryMode = "Replace" | "Append" | "Prepend" | 0 | 1 | 2;

export type LogMessage = {
  content: string;
  batchId: string;
  jobId: string;
  sessionId: string;
  deliveryMode?: LogDeliveryMode;
};

type BatchLogsSubscription = {
  jobId: string;
  jobPipelineStepId: string;
  batchId: string;
  sessionId: string;
};

type LogMessageListener = (message: LogMessage) => void;

const logMessageListeners = new Set<LogMessageListener>();
const batchSubscriptions = new Map<string, BatchLogsSubscription>();
const batchSubscriptionCounts = new Map<string, number>();

let connection: HubConnection | null = null;
let startPromise: Promise<void> | null = null;

const getSubscriptionKey = ({ batchId, sessionId }: Pick<BatchLogsSubscription, "batchId" | "sessionId">) => `${batchId}:${sessionId}`;

const getConnectionUrl = () => runtimeConfig.API_BASE_URL.replace("/swagger", "") + API_ENDPOINTS.logsHub;

const dispatchLogMessage = (message?: LogMessage) => {
  if (!message?.batchId || !message.sessionId) return;
  logMessageListeners.forEach((listener) => listener(message));
};

const getOrCreateConnection = () => {
  if (connection) {
    return connection;
  }

  connection = new HubConnectionBuilder()
    .withUrl(getConnectionUrl(), {
      transport: HttpTransportType.ServerSentEvents,
    })
    .withAutomaticReconnect()
    .build();

  connection.on("ReceiveLogs", dispatchLogMessage);
  connection.onreconnected(() => resubscribe());
  connection.onclose(() => {
    startPromise = null;
  });

  return connection;
};

const startConnection = async () => {
  const currentConnection = getOrCreateConnection();

  if (currentConnection.state === "Connected") {
    return;
  }

  if (startPromise) {
    return startPromise;
  }

  startPromise = currentConnection.start().then(() => {
    startPromise = null;
  }).catch((error) => {
    startPromise = null;
    console.error("Logs hub connection error:", error);
    throw error;
  });

  return startPromise;
};

const invokeWhenConnected = async (methodName: string, ...args: unknown[]) => {
  await startConnection();
  await getOrCreateConnection().invoke(methodName, ...args);
};

const resubscribe = async () => {
  for (const subscription of batchSubscriptions.values()) {
    await invokeWhenConnected("SubscribeBatchLogs", subscription.jobId, subscription.jobPipelineStepId, subscription.batchId, subscription.sessionId);
  }
};

const stopConnectionIfIdle = () => {
  if (logMessageListeners.size > 0 || batchSubscriptions.size > 0) {
    return;
  }

  const currentConnection = connection;
  connection = null;
  startPromise = null;

  if (currentConnection) {
    currentConnection.off("ReceiveLogs", dispatchLogMessage);
    void currentConnection.stop().catch((error) => {
      console.error("Error stopping logs hub connection:", error);
    });
  }
};

export const subscribeToLogMessages = (listener: LogMessageListener) => {
  logMessageListeners.add(listener);
  void startConnection().catch(() => undefined);

  return () => {
    logMessageListeners.delete(listener);
    stopConnectionIfIdle();
  };
};

export const subscribeToBatchLogs = (subscription: BatchLogsSubscription) => {
  const key = getSubscriptionKey(subscription);
  const nextCount = (batchSubscriptionCounts.get(key) ?? 0) + 1;

  batchSubscriptionCounts.set(key, nextCount);
  batchSubscriptions.set(key, subscription);

  if (nextCount === 1) {
    void invokeWhenConnected("SubscribeBatchLogs", subscription.jobId, subscription.jobPipelineStepId, subscription.batchId, subscription.sessionId).catch((error) => {
      console.error(`Error subscribing to batch logs for ${subscription.batchId}:`, error);
    });
  }

  return () => {
    const currentCount = batchSubscriptionCounts.get(key) ?? 0;

    if (currentCount <= 1) {
      batchSubscriptionCounts.delete(key);
      batchSubscriptions.delete(key);
      void invokeWhenConnected("UnsubscribeBatchLogs", subscription.batchId, subscription.sessionId).catch((error) => {
        console.error(`Error unsubscribing from batch logs for ${subscription.batchId}:`, error);
      });
    } else {
      batchSubscriptionCounts.set(key, currentCount - 1);
    }

    stopConnectionIfIdle();
  };
};
